import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { salesApi, usersApi } from '../../services/api';
import { useAuth } from '../../store/authContext';
import { formatCurrency } from '../../utils/formatters';
import './SaleCreate.css';

const UNIT_TYPES = [
  { value: 'flat', label: 'Flat' },
  { value: 'shop', label: 'Shop' },
  { value: 'plot', label: 'Plot' },
];

const SALE_TYPES = [
  { value: 'fresh', label: 'Fresh' },
  { value: 'resale', label: 'Resale' },
];

export default function SaleCreate() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    client_name: '',
    pan_number: '',
    unit_type: 'flat',
    sale_type: 'fresh',
    project_name: '',
    size: '',
    config: '',
    price_per_sqft: '',
    plc: '',
    other_charges: '',
    total_sales_value: '',
    brokerage_type: 'percentage',
    brokerage_percentage: '',
    brokerage_amount: '',
    brokerage_remark: '',
    customer_passback: '',
    broker_passback: '',
    agent_share_type: 'percentage',
    agent_share_percentage: '',
    agent_share_amount: '',
    agent_share_remark: '',
    brokerage_received: '',
    agent_id: '',
    booking_date: '',
    notes: '',
  });

  const fetchUsers = useCallback(async () => {
    if (currentUser?.role !== 'admin') return;
    try {
      const res = await usersApi.list();
      setUsers(res.data);
    } catch {
      // Non-critical
    }
  }, [currentUser?.role]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setNum = (field) => (e) => {
    const val = e.target.value;
    if (val === '' || /^[0-9]*\.?[0-9]*$/.test(val)) {
      setForm((f) => ({ ...f, [field]: val }));
    }
  };
  const setInt = (field) => (e) => {
    const val = e.target.value;
    if (val === '' || /^[0-9]+$/.test(val)) {
      setForm((f) => ({ ...f, [field]: val }));
    }
  };

  // Computed values
  const tsv = parseInt(form.total_sales_value) || 0;
  const brokerageAmount = useMemo(() => {
    if (form.brokerage_type === 'percentage') {
      const pct = parseFloat(form.brokerage_percentage) || 0;
      return Math.floor(tsv * pct / 100);
    }
    return parseInt(form.brokerage_amount) || 0;
  }, [form.brokerage_type, form.brokerage_percentage, form.brokerage_amount, tsv]);

  const customerPassback = parseInt(form.customer_passback) || 0;
  const brokerPassback = parseInt(form.broker_passback) || 0;
  const brokerageEarned = brokerageAmount - customerPassback - brokerPassback;

  const agentShareAmount = useMemo(() => {
    if (form.agent_share_type === 'percentage') {
      const pct = parseFloat(form.agent_share_percentage) || 0;
      return Math.floor(Math.max(brokerageEarned, 0) * pct / 100);
    }
    return parseInt(form.agent_share_amount) || 0;
  }, [form.agent_share_type, form.agent_share_percentage, form.agent_share_amount, brokerageEarned]);

  const companyShare = brokerageEarned - agentShareAmount;

  const needsProjectName = form.unit_type === 'flat' || form.unit_type === 'shop';
  const isFlat = form.unit_type === 'flat';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const data = {
        client_name: form.client_name,
        pan_number: form.pan_number.toUpperCase(),
        unit_type: form.unit_type,
        sale_type: form.sale_type,
        total_sales_value: parseInt(form.total_sales_value),
        brokerage_type: form.brokerage_type,
        customer_passback: parseInt(form.customer_passback) || 0,
        broker_passback: parseInt(form.broker_passback) || 0,
        brokerage_received: parseInt(form.brokerage_received) || 0,
        agent_share_type: form.agent_share_type,
        booking_date: form.booking_date,
      };

      if (form.project_name) data.project_name = form.project_name;
      if (form.size) data.size = parseFloat(form.size);
      if (form.config) data.config = isFlat ? `${form.config} BHK` : form.config;
      if (form.price_per_sqft) data.price_per_sqft = parseFloat(form.price_per_sqft);
      if (form.plc) data.plc = parseFloat(form.plc);
      if (form.other_charges) data.other_charges = parseFloat(form.other_charges);

      if (form.brokerage_type === 'percentage') {
        data.brokerage_percentage = parseFloat(form.brokerage_percentage);
      } else {
        data.brokerage_amount = parseInt(form.brokerage_amount);
        data.brokerage_remark = form.brokerage_remark;
      }

      if (form.agent_share_type === 'percentage') {
        data.agent_share_percentage = parseFloat(form.agent_share_percentage);
      } else {
        data.agent_share_amount = parseInt(form.agent_share_amount);
        data.agent_share_remark = form.agent_share_remark;
      }

      if (currentUser?.role === 'admin' && form.agent_id) {
        data.agent_id = form.agent_id;
      }

      const res = await salesApi.create(data);
      if (form.notes.trim()) {
        await salesApi.addNote(res.data.id, form.notes.trim());
      }
      navigate(`/sales/${res.data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg || d).join('. '));
      } else {
        setError(detail || 'Failed to create sale.');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const activeAgents = users.filter(u => u.is_active && u.assigned_modules?.includes('sales'));

  return (
    <div className="sc-page">
      {/* Breadcrumb */}
      <div className="breadcrumb animate-fade-up">
        <Link to="/sales" className="breadcrumb-link">Sales</Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-current">New Sale</span>
      </div>

      <h1 className="page-title animate-fade-up" style={{ animationDelay: '40ms', marginBottom: 24 }}>
        Register New Sale
      </h1>

      {error && <div className="sc-error animate-fade-up">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Section 1: Client & Project Details */}
        <div className="card sc-section animate-fade-up" style={{ animationDelay: '80ms' }}>
          <h3 className="sc-section-title">Client & Project Details</h3>

          <div className="sc-form-row">
            <div className="form-group">
              <label className="section-label">Client Name *</label>
              <input className="input" value={form.client_name} onChange={set('client_name')} placeholder="Full name" required />
            </div>
            <div className="form-group">
              <label className="section-label">PAN Number *</label>
              <input
                className="input sc-pan"
                value={form.pan_number}
                onChange={set('pan_number')}
                placeholder="ABCDE1234F"
                maxLength={10}
                required
                style={{ textTransform: 'uppercase' }}
              />
            </div>
          </div>

          <div className="sc-form-row">
            <div className="form-group">
              <label className="section-label">Booking Date *</label>
              <input className="input" type="date" value={form.booking_date} onChange={set('booking_date')} required />
            </div>
          </div>

          <div className="sc-form-row">
            <div className="form-group">
              <label className="section-label">Unit Type *</label>
              <select className="input sc-select" value={form.unit_type} onChange={set('unit_type')}>
                {UNIT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="section-label">Sale Type *</label>
              <select className="input sc-select" value={form.sale_type} onChange={set('sale_type')}>
                {SALE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div className="sc-form-row">
            {needsProjectName && (
              <div className="form-group">
                <label className="section-label">Project Name *</label>
                <input className="input" value={form.project_name} onChange={set('project_name')} placeholder="e.g. Prestige Lakeside" required={needsProjectName} />
              </div>
            )}
            {!needsProjectName && (
              <div className="form-group">
                <label className="section-label">Project Name</label>
                <input className="input" value={form.project_name} onChange={set('project_name')} placeholder="Optional" />
              </div>
            )}
            <div className="form-group">
              <label className="section-label">Size (sq. ft.)</label>
              <input className="input" value={form.size} onChange={setNum('size')} placeholder="e.g. 1200" />
            </div>
          </div>

          <div className="sc-form-row">
            {isFlat && (
              <div className="form-group">
                <label className="section-label">Config</label>
                <div className="sc-bhk-wrap">
                  <input
                    className="input sc-bhk-input"
                    value={form.config}
                    onChange={setNum('config')}
                    placeholder="e.g. 2"
                  />
                  <span className="sc-bhk-label">BHK</span>
                </div>
              </div>
            )}
            {!isFlat && (
              <div className="form-group">
                <label className="section-label">Config</label>
                <input className="input" value={form.config} onChange={set('config')} placeholder="Configuration" />
              </div>
            )}
            <div className="form-group">
              <label className="section-label">Price (per sq. ft.)</label>
              <input className="input" value={form.price_per_sqft} onChange={setNum('price_per_sqft')} placeholder="0" />
            </div>
          </div>

          <div className="sc-form-row sc-form-row--3">
            <div className="form-group">
              <label className="section-label">PLC</label>
              <input className="input" value={form.plc} onChange={setNum('plc')} placeholder="0" />
            </div>
            <div className="form-group">
              <label className="section-label">Other Charges</label>
              <input className="input" value={form.other_charges} onChange={setNum('other_charges')} placeholder="0" />
            </div>
            <div className="form-group">
              <label className="section-label">Total Sales Value *</label>
              <input className="input" value={form.total_sales_value} onChange={setInt('total_sales_value')} placeholder="0" required />
              {tsv > 0 && <p className="sc-computed-hint">{formatCurrency(tsv)}</p>}
            </div>
          </div>
        </div>

        {/* Section 2: Brokerage */}
        <div className="card sc-section animate-fade-up" style={{ animationDelay: '120ms' }}>
          <h3 className="sc-section-title">Brokerage</h3>

          <div className="sc-form-row">
            <div className="form-group">
              <label className="section-label">Brokerage Type *</label>
              <div className="sc-toggle-group">
                <button
                  type="button"
                  className={`sc-toggle ${form.brokerage_type === 'percentage' ? 'sc-toggle--active' : ''}`}
                  onClick={() => setForm(f => ({ ...f, brokerage_type: 'percentage', brokerage_amount: '', brokerage_remark: '' }))}
                >
                  Percentage
                </button>
                <button
                  type="button"
                  className={`sc-toggle ${form.brokerage_type === 'lumpsum' ? 'sc-toggle--active' : ''}`}
                  onClick={() => setForm(f => ({ ...f, brokerage_type: 'lumpsum', brokerage_percentage: '' }))}
                >
                  Lumpsum
                </button>
              </div>
            </div>

            {form.brokerage_type === 'percentage' ? (
              <div className="form-group">
                <label className="section-label">Brokerage % *</label>
                <div className="sc-pct-wrap">
                  <input className="input sc-pct-input" value={form.brokerage_percentage} onChange={setNum('brokerage_percentage')} placeholder="e.g. 2" required />
                  <span className="sc-pct-label">%</span>
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label className="section-label">Brokerage Amount *</label>
                <input className="input" value={form.brokerage_amount} onChange={setInt('brokerage_amount')} placeholder="0" required />
              </div>
            )}
          </div>

          {form.brokerage_type === 'percentage' && brokerageAmount > 0 && (
            <div className="sc-computed-row">
              <span className="sc-computed-label">Brokerage Amount</span>
              <span className="sc-computed-value">{formatCurrency(brokerageAmount)}</span>
            </div>
          )}

          {form.brokerage_type === 'lumpsum' && (
            <div className="form-group">
              <label className="section-label">Remark (explain calculation) *</label>
              <textarea className="input sc-textarea" value={form.brokerage_remark} onChange={set('brokerage_remark')} rows={2} placeholder="Explain the brokerage calculation..." required />
            </div>
          )}

          <div className="sc-form-row">
            <div className="form-group">
              <label className="section-label">Customer Passback *</label>
              <input className="input" value={form.customer_passback} onChange={setInt('customer_passback')} placeholder="0" required />
            </div>
            <div className="form-group">
              <label className="section-label">Broker Passback *</label>
              <input className="input" value={form.broker_passback} onChange={setInt('broker_passback')} placeholder="0" required />
            </div>
          </div>

          <div className={`sc-computed-row sc-computed-row--highlight ${brokerageEarned < 0 ? 'sc-computed-row--error' : ''}`}>
            <span className="sc-computed-label">Brokerage Earned</span>
            <span className="sc-computed-value">{formatCurrency(brokerageEarned)}</span>
          </div>
          {brokerageEarned < 0 && (
            <p className="sc-validation-error">Passbacks exceed brokerage amount</p>
          )}

          <div className="sc-form-row" style={{ marginTop: 16 }}>
            <div className="form-group">
              <label className="section-label">Brokerage Received</label>
              <input className="input" value={form.brokerage_received} onChange={setInt('brokerage_received')} placeholder="0" />
              {(parseInt(form.brokerage_received) || 0) > 0 && (
                <p className="sc-computed-hint">{formatCurrency(parseInt(form.brokerage_received))}</p>
              )}
            </div>
          </div>
        </div>

        {/* Section 3: Agent Share */}
        <div className="card sc-section animate-fade-up" style={{ animationDelay: '160ms' }}>
          <h3 className="sc-section-title">Agent Share</h3>

          <div className="sc-form-row">
            <div className="form-group">
              <label className="section-label">Agent Share Type *</label>
              <div className="sc-toggle-group">
                <button
                  type="button"
                  className={`sc-toggle ${form.agent_share_type === 'percentage' ? 'sc-toggle--active' : ''}`}
                  onClick={() => setForm(f => ({ ...f, agent_share_type: 'percentage', agent_share_amount: '', agent_share_remark: '' }))}
                >
                  Percentage
                </button>
                <button
                  type="button"
                  className={`sc-toggle ${form.agent_share_type === 'lumpsum' ? 'sc-toggle--active' : ''}`}
                  onClick={() => setForm(f => ({ ...f, agent_share_type: 'lumpsum', agent_share_percentage: '' }))}
                >
                  Lumpsum
                </button>
              </div>
            </div>

            {form.agent_share_type === 'percentage' ? (
              <div className="form-group">
                <label className="section-label">Agent Share % *</label>
                <div className="sc-pct-wrap">
                  <input className="input sc-pct-input" value={form.agent_share_percentage} onChange={setNum('agent_share_percentage')} placeholder="e.g. 50" required />
                  <span className="sc-pct-label">%</span>
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label className="section-label">Agent Share Amount *</label>
                <input className="input" value={form.agent_share_amount} onChange={setInt('agent_share_amount')} placeholder="0" required />
              </div>
            )}
          </div>

          {form.agent_share_type === 'percentage' && agentShareAmount > 0 && (
            <div className="sc-computed-row">
              <span className="sc-computed-label">Agent Share Amount</span>
              <span className="sc-computed-value">{formatCurrency(agentShareAmount)}</span>
            </div>
          )}

          {form.agent_share_type === 'lumpsum' && (
            <div className="form-group">
              <label className="section-label">Remark (explain calculation) *</label>
              <textarea className="input sc-textarea" value={form.agent_share_remark} onChange={set('agent_share_remark')} rows={2} placeholder="Explain the agent share calculation..." required />
            </div>
          )}

          {currentUser?.role === 'admin' && (
            <div className={`sc-computed-row sc-computed-row--highlight ${companyShare < 0 ? 'sc-computed-row--error' : ''}`}>
              <span className="sc-computed-label">Company Share</span>
              <span className="sc-computed-value">{formatCurrency(companyShare)}</span>
            </div>
          )}
          {companyShare < 0 && currentUser?.role === 'admin' && (
            <p className="sc-validation-error">Agent share exceeds brokerage earned</p>
          )}

          {currentUser?.role === 'admin' && activeAgents.length > 0 && (
            <div className="form-group" style={{ marginTop: 16 }}>
              <label className="section-label">Select Agent *</label>
              <select className="input sc-select" value={form.agent_id} onChange={set('agent_id')} required>
                <option value="">Choose an agent...</option>
                {activeAgents.map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Section 4: Notes */}
        <div className="card sc-section animate-fade-up" style={{ animationDelay: '200ms' }}>
          <h3 className="sc-section-title">Notes</h3>
          <div className="form-group">
            <textarea
              className="input sc-textarea"
              value={form.notes}
              onChange={set('notes')}
              rows={3}
              placeholder="Add any notes about this sale..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sc-footer animate-fade-up" style={{ animationDelay: '240ms' }}>
          <Link to="/sales" className="btn btn-ghost">Cancel</Link>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || brokerageEarned < 0 || (currentUser?.role === 'admin' && companyShare < 0)}
          >
            {saving ? 'Creating...' : 'Create Sale'}
          </button>
        </div>
      </form>
    </div>
  );
}
